import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { IconArrowRight, IconChevronDown, IconChevronUp, IconClock } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLink } from '@/components/ui/tee-link';
import { useIssueNoticesLive } from '@/hooks/useIssueNotices';
import { showSuccessToast } from '@/utils/toast.utils';
import type { IssueNotice, IssueNoticeLive } from '@/types/issue-notice';
import { IssueCountdown } from './IssueCountdown';
import { SEVERITY_META, categoryLabel, scopeLabel } from './issue-notice-meta';
import { cn } from '@/utils/cn';

interface RowProps {
  notice: IssueNotice;
  serverOffsetMs: number;
  primary?: boolean;
  /** Top row on the solid bar: light text on the severity colour. */
  solid?: boolean;
  onOpen: (e: MouseEvent<HTMLAnchorElement>, id: string) => void;
}

function BannerRow({ notice, serverOffsetMs, primary, solid, onOpen }: RowProps) {
  const { t } = useTranslation();
  const meta = SEVERITY_META[notice.severity];
  const { Icon } = meta;
  const expiring = notice.status === 'expiring_soon';
  // TeeLink ignores `style`, so link colours go through classes.
  const linkColor = solid
    ? 'text-[color:var(--text-on-severity)] hover:text-[color:var(--text-on-severity-muted)]'
    : 'text-[color:var(--text-content-title)] hover:text-[color:var(--text-content-sub-title)]';
  const muted = solid ? 'var(--text-on-severity-muted)' : 'var(--text-content-sub-title)';
  return (
    <div className={cn('flex min-w-0 items-center gap-3 md:gap-4', !primary && 'py-2')}>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold uppercase tracking-[0.06em]',
          primary && solid && notice.severity === 'critical' && 'motion-safe:animate-pulse',
        )}
        style={solid ? { color: meta.fg, backgroundColor: 'var(--text-on-severity)' } : { color: meta.fg, backgroundColor: meta.bg }}
      >
        <Icon size={primary ? 18 : 16} aria-hidden />
        <span className="hidden sm:inline">{t(`issues.severity.${notice.severity}`)}</span>
        <span className="sr-only sm:hidden">{t(`issues.severity.${notice.severity}`)}</span>
      </span>
      <TeeLink
        href={`/issues/${notice.id}`}
        onClick={(e) => onOpen(e, notice.id)}
        variant="secondary"
        size="m"
        title={notice.title}
        className={cn('min-w-0 max-w-full justify-start font-semibold', primary ? 'text-[16px]' : 'text-[14px]', linkColor)}
      >
        {/* The link is a flex box, so the ellipsis has to sit on an inner span. */}
        <span className="block min-w-0 truncate">{notice.title}</span>
      </TeeLink>
      <span className="hidden md:inline shrink-0 max-w-[30%] truncate text-[14px]" style={{ color: muted }}>
        {scopeLabel(notice, t('issues.allLocations'))}
      </span>
      <span
        className={cn(
          'ml-auto hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold tabular-nums',
        )}
        style={
          solid
            ? { color: 'var(--text-on-severity)', backgroundColor: 'var(--background-on-severity-chip)' }
            : { color: expiring ? meta.fg : 'var(--text-content-title)', backgroundColor: 'var(--background-card)' }
        }
      >
        <IconClock size={14} aria-hidden className={cn(expiring && 'motion-safe:animate-pulse')} />
        <IssueCountdown target={notice.endsAt} serverOffsetMs={serverOffsetMs} />
      </span>
      {primary && (
        <TeeLink
          href={`/issues/${notice.id}`}
          onClick={(e) => onOpen(e, notice.id)}
          variant="secondary"
          size="s"
          rightIcon={<IconArrowRight size={14} />}
          className={cn('hidden lg:inline-flex shrink-0 font-semibold underline underline-offset-2', linkColor)}
        >
          {t('issues.viewDetails')}
        </TeeLink>
      )}
    </div>
  );
}

/** Toast when a notice leaves the list while on screen (UI-7). */
function useEndedToasts(data: IssueNoticeLive | undefined) {
  const { t } = useTranslation();
  const previous = useRef<Map<string, IssueNotice> | null>(null);
  useEffect(() => {
    if (!data) return;
    const current = new Map(data.active.map((n) => [n.id, n]));
    if (previous.current) {
      for (const [id, notice] of previous.current) {
        if (current.has(id)) continue;
        const resolved = data.unreadAlerts.some((a) => a.noticeId === id && a.eventType === 'resolved');
        showSuccessToast(
          t(resolved ? 'issues.toast.resolved' : 'issues.toast.ended', { category: categoryLabel(notice), title: notice.title }),
          5000,
        );
      }
    }
    previous.current = current;
  }, [data, t]);
}

/**
 * The permanent issue banner shown above every page (UI-1). It cannot be
 * dismissed (UI-6); it goes away only when the notice ends.
 */
export function IssueNoticeBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, stale, serverOffsetMs } = useIssueNoticesLive();
  const [expanded, setExpanded] = useState(false);
  useEndedToasts(data);

  const active = data?.active ?? [];
  // Scheduled notices stay in the Issue Centre's Upcoming tab, not here.
  if (!active.length) return null;

  const open = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setExpanded(false);
    navigate(`/issues/${id}`);
  };

  const [top, ...rest] = active;
  const meta = top ? SEVERITY_META[top.severity] : SEVERITY_META.info;
  // Every severity fills the bar; tints were too easy to miss on the cream page.
  const solid = !!top;
  const role = top?.severity === 'critical' ? 'alert' : 'status';

  return (
    <div
      className="flex-shrink-0 border-b border-[var(--border-default)]"
      style={solid ? { backgroundColor: meta.fg } : { backgroundColor: meta.bg, boxShadow: `inset 6px 0 0 ${meta.fg}` }}
      data-testid="issue-notice-banner"
    >
      <div className="mx-auto w-full max-w-[var(--page-max-width)] px-4 md:px-10 py-3">
        {top && (
          <div role={role} aria-live={role === 'alert' ? 'assertive' : 'polite'} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <BannerRow notice={top} serverOffsetMs={serverOffsetMs} primary solid={solid} onOpen={open} />
            </div>
            {rest.length > 0 && (
              <TeeButton
                variant="ghost"
                size="s"
                className="shrink-0 font-semibold"
                style={{ color: solid ? 'var(--text-on-severity)' : 'var(--text-content-title)' }}
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
                rightIcon={expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                text={t('issues.moreIssues', { count: rest.length })}
              />
            )}
          </div>
        )}

        <AnimatePresence initial={false}>
          {expanded && rest.length > 0 && (
            <motion.div
              key="more"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-1 max-h-[40vh] overflow-y-auto divide-y divide-[var(--border-default)] rounded-[6px] bg-[var(--background-card)] px-3">
                <AnimatePresence initial={false}>
                  {rest.map((n) => (
                    <motion.div key={n.id} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
                      <BannerRow notice={n} serverOffsetMs={serverOffsetMs} onOpen={open} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {stale && <p className="mt-1 text-[12px]" style={{ color: solid ? 'var(--text-on-severity-muted)' : 'var(--text-content-sub-title)' }}>{t('issues.mayBeOutOfDate')}</p>}
      </div>
    </div>
  );
}
